import React, { useCallback, useRef } from "react";
import {
  AHIcon,
  AHSeparator,
  AHText,
  Box,
  resolveAHTextStyle,
  TouchableBox,
  useLayoutContext,
  useScaleFactor,
  useTheme,
} from "arkh";
import { Platform, TextInputProps, View } from "react-native";
import { useIntlTokens } from "../../components/mobile/useIntlTokens";
import { TextInput } from "react-native-gesture-handler";
import { useFocusEffect } from "@react-navigation/native";

export function SearchInput({
  query,
  isSearching,
  autoFocus,
  showCancel,
  placeholder,
  onChange,
  onFocus,
  onBlur,
  returnKeyType,
  inputComponent,
  hideSeparator,
}: {
  hideSeparator?: boolean;
  query: string;
  isSearching: boolean;
  autoFocus?: boolean;
  showCancel?: boolean;
  placeholder?: string;
  returnKeyType?: TextInputProps["returnKeyType"];
  autoCapitalize?: TextInputProps["autoCapitalize"];
  onChange?: TextInputProps["onChange"];
  onFocus?: () => void;
  onBlur?: () => void;
  inputComponent?: React.ComponentType<TextInputProps>;
}) {
  const layout = useLayoutContext();

  const intl_tokens = useIntlTokens();
  const ref = useRef<TextInput>(null);
  useFocusEffect(
    useCallback(() => {
      if (autoFocus) {
        if (Platform.OS === "web") {
          setTimeout(() => {
            ref.current?.focus();
          }, 10);
        } else {
          ref.current?.focus();
        }
      }
      return () => {
        ref.current?.blur();
      };
    }, [ref, autoFocus])
  );

  const sf = useScaleFactor();

  const theme = useTheme();

  const InputComponent = inputComponent ?? TextInput;

  return (
    <>
      <Box
        cornerRadiusTopLeft="large"
        cornerRadiusTopRight="large"
        cornerRadiusBottomLeft={isSearching ? "none" : "large"}
        cornerRadiusBottomRight={isSearching ? "none" : "large"}
        color="surface-primary"
        style={{
          flexDirection: "row",
          alignItems: "center",
        }}
      >
        <Box style={{ flex: 1 }}>
          <InputComponent
            ref={ref}
            underlineColorAndroid="transparent"
            autoCorrect={false}
            placeholderTextColor={theme.washedText}
            autoCapitalize="none"
            style={[
              resolveAHTextStyle("list-item-title", sf, theme, layout),

              {
                padding: layout.space.medium,
                paddingVertical: layout.space.small,
                borderRadius: layout.radius.large,
                backgroundColor: theme["surface-primary"],

                borderColor: theme["border-primary"],
                margin: layout.space.small,
                paddingLeft:
                  layout.circle.smallMedium +
                  layout.space.medium +
                  layout.space.small,
                fontWeight: "normal", // fixes android custom fonts :S
              },
            ]}
            autoFocus={autoFocus}
            value={query}
            onChange={onChange}
            returnKeyType={returnKeyType ?? "search"}
            clearButtonMode="never"
            placeholder={placeholder ?? intl_tokens.Search}
            onFocus={onFocus}
            onBlur={Platform.OS === "web" ? () => {} : onBlur}
          />

          <View
            pointerEvents="none"
            style={{
              position: "absolute",
              left: 0,
              bottom: 0,
              top: 0,
              alignItems: "center",
              flexDirection: "row",
            }}
          >
            <AHIcon
              spaceLeft="medium"
              type="search"
              color="text-washed"
              size="large"
            />
          </View>
        </Box>
        {showCancel && isSearching ? (
          <TouchableBox
            spaceHorizontal="medium"
            hitSlop={{
              left: 40,
              top: 30,
              bottom: 20,
              right: 10,
            }}
            cornerRadiusTopRight="large"
            style={{
              justifyContent: "center",
            }}
            color="surface-primary"
            onPress={() => {
              ref.current?.clear();
              ref.current?.blur();
              if (Platform.OS === "web") {
                onBlur?.();
              }
            }}
          >
            <AHText color="primary">{intl_tokens.Cancel}</AHText>
          </TouchableBox>
        ) : null}
      </Box>
      {!hideSeparator && isSearching ? <AHSeparator /> : null}
    </>
  );
}
